import React, { useContext } from 'react'

import NotListedLocationOutlinedIcon from '@mui/icons-material/NotListedLocationOutlined'
import { Card, Checkbox, Divider, Icon, IconButton } from '@mui/material'
import Grid from '@mui/material/Grid'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import FormField from 'modules/FormField'

import { AmbiguousLocationsFormContext } from '../constants'

function MainForm() {
	const { ambiguousLocationsForm, setAmbiguousLocationsForm, setFormEdited } = useContext(
		AmbiguousLocationsFormContext
	)

	const updateLocation = (index: number, updatedItem: any) => {
		const newList = [...ambiguousLocationsForm]
		newList[index] = updatedItem
		setAmbiguousLocationsForm(newList)
		setFormEdited(true)
	}

	const addLocation = () => {
		setAmbiguousLocationsForm([{ name: '', suggestions: undefined }, ...ambiguousLocationsForm])
	}

	const removeLocation = (index: number) => {
		setAmbiguousLocationsForm(ambiguousLocationsForm.filter((_, i) => i !== index))
		setFormEdited(true)
	}

	return (
		<Card>
			<MDBox p={3}>
				<MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
					<MDBox>
						<MDBox display="flex" alignItems="center" gap={1}>
							<NotListedLocationOutlinedIcon color="info" />
							<MDTypography variant="h5" fontWeight="medium">
								Ambiguous Locations
							</MDTypography>
						</MDBox>
						<MDTypography variant="button" color="text">
							Define locations that require AI clarification (e.g., specific station exits or
							multi-branch vendors).
						</MDTypography>
					</MDBox>
					<IconButton onClick={addLocation} color="info">
						<Icon>add_circle_outlined</Icon>
					</IconButton>
				</MDBox>

				<Divider />

				<Grid container spacing={3} mt={1}>
					{ambiguousLocationsForm.map((location, index) => (
						<Grid item xs={12} key={index}>
							<MDBox p={2} sx={{ border: '1px solid #eee', borderRadius: '12px' }}>
								{/* Main Location Row */}
								<MDBox display="flex" alignItems="center" gap={2} mb={1}>
									<MDBox display={'flex'} flex={4} flexDirection={'column'}>
										{index === 0 && (
											<MDTypography variant="button" fontWeight="bold">
												Name
											</MDTypography>
										)}
										<MDBox display={'flex'} alignItems={'center'} gap={2}>
											<MDAvatar bgColor="secondary" size="xs">
												{index + 1}
											</MDAvatar>
											<MDBox flex={1}>
												<FormField
													placeholder="Enter the name of the ambiguous location"
													value={location.name}
													onChange={(e: any) =>
														updateLocation(index, { ...location, name: e.target.value })
													}
												/>
											</MDBox>
										</MDBox>
									</MDBox>
									<MDBox
										display={'flex'}
										flex={1}
										flexDirection={'column'}
										justifyContent={'center'}
										alignItems={'center'}
									>
										{index === 0 && (
											<MDTypography variant="button" fontWeight="bold">
												Provide Suggestions
											</MDTypography>
										)}
										<Checkbox
											checked={!!location.suggestions}
											onChange={e => {
												const isChecked = e.target.checked

												updateLocation(index, {
													...location,
													suggestions: isChecked ? [''] : undefined,
												})
											}}
										/>
									</MDBox>
									<IconButton size="small" color="error" onClick={() => removeLocation(index)}>
										<Icon>close</Icon>
									</IconButton>
								</MDBox>

								{/* Suggestions Sub-List */}
								{!!location.suggestions && (
									<MDBox ml={8} mt={2} display="flex" flexDirection="column" gap={1.5}>
										<MDTypography variant="caption" fontWeight="bold" color="info">
											SUGGESTED PICKUP POINTS (MAX 3)
										</MDTypography>

										{location.suggestions.map((suggestion, sIndex) => (
											<MDBox key={sIndex} display="flex" alignItems="center" gap={1} mr={25}>
												<MDBox flex={10}>
													<FormField
														placeholder={`AI Suggestion ${sIndex + 1}`}
														value={suggestion}
														onChange={(e: any) => {
															const newSugg = [...location.suggestions]
															newSugg[sIndex] = e.target.value
															updateLocation(index, { ...location, suggestions: newSugg })
														}}
													/>
												</MDBox>
												<MDBox display={'flex'} flex={1}>
													{sIndex !== 0 && (
														<IconButton
															size="small"
															onClick={() => {
																const newSugg = location.suggestions.filter((_, i) => i !== sIndex)
																updateLocation(index, { ...location, suggestions: newSugg })
															}}
														>
															<Icon fontSize="small">close</Icon>
														</IconButton>
													)}
												</MDBox>
											</MDBox>
										))}

										{location.suggestions.length < 3 && (
											<MDBox display="flex" justifyContent="flex-start">
												<MDTypography
													variant="button"
													color="info"
													fontWeight="medium"
													sx={{
														cursor: 'pointer',
														display: 'flex',
														alignItems: 'center',
														gap: 0.5,
													}}
													onClick={() => {
														updateLocation(index, {
															...location,
															suggestions: [...location.suggestions, ''],
														})
													}}
												>
													<Icon fontSize="small">add</Icon> Add Suggestion
												</MDTypography>
											</MDBox>
										)}
									</MDBox>
								)}
							</MDBox>
						</Grid>
					))}
				</Grid>
			</MDBox>
		</Card>
	)
}

export default MainForm
